/*
=================================
HTTP CLIENT
=================================

Every request in the app goes through `apiClient`. It carries the access token,
refreshes it once on a 401, and knows which requests and pages must stay
anonymous. `publicApiClient` is the same base URL with none of that attached —
for the token refresh itself (which cannot recurse through the interceptor) and
for reads made on behalf of a visitor who has no session.
*/

import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
// Imported from the module rather than the `router` barrel: that barrel pulls in
// the whole page tree, which every module that talks to the API would then drag
// along with it.
import { Routes } from "router/routes";
import { preservePendingRole } from "utils/pendingHiringIntent";

const baseURL = process.env.REACT_APP_API_BASE_URL;

/** The authenticated client. Use this for everything unless a request must be anonymous. */
export const apiClient = axios.create({ baseURL });

/**
 * Interceptor-free client: no token is attached and a 401 is handed back
 * untouched. For the refresh call (which would otherwise recurse) and for
 * requests made on behalf of a visitor with no session.
 */
export const publicApiClient = axios.create({ baseURL });

const ACCESS_TOKEN_KEY = "precisionAccess";
const REFRESH_TOKEN_KEY = "precisionRefresh";

// Endpoints that don't require authentication.
const publicEndpoints = [
  "/auth/confirm-email/",
  "/auth/resend-email-verification/",
  "/auth/login/",
  "/auth/signup/",
  "/auth/request-reset-password/",
  "/auth/reset-password/",
  "/billing/plans/",
];

// Endpoints that belong to an assessment link rather than to an account, and so
// must go out anonymously wherever they are fired from. Matched by pattern
// because the id sits in the middle of the path.
const publicEndpointPatterns = [
  // Candidate feedback on a finished assessment.
  /\/candidate\/assessment-responses\/[^/]+\/review\/?$/,
];

// Public pages where requests must behave like an anonymous visitor (incognito),
// regardless of any token that happens to be in localStorage. Shared report links
// and the whole candidate journey are reached by link and belong to no account, so
// attaching a stale token — or a recruiter's own token while testing the flow —
// wrongly triggers a 401, and the 401 handler below would then wipe local storage
// and bounce the visitor to sign-in mid-assessment. Only employer sign-in ever
// writes an access token, so no visitor on these pages has one to lose.
const PUBLIC_PAGE_PREFIXES = [
  "/shared-report",
  "/candidate-assessment",
  "/take-assessment",
  "/assessment-feedback",
];

const isPublicPage = () =>
  PUBLIC_PAGE_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));

const isPublicRequest = (url?: string) =>
  publicEndpoints.some((endpoint) => url?.includes(endpoint)) ||
  publicEndpointPatterns.some((pattern) => pattern.test(url ?? ""));

// Only the employer dashboard requires a session — every protected route lives
// under it. Elsewhere (landing pages, auth screens, candidate links) a failed
// request must leave the visitor on the page they are looking at rather than
// bouncing them to sign-in.
const requiresSession = () => window.location.pathname.startsWith("/employer");

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (isPublicRequest(config.url) || isPublicPage()) return config;

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (accessToken && config.headers) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const REFRESH_URL = "/auth/token/refresh/";

/**
 * Exchanges the stored refresh token for a new access token. Returns an empty
 * string when the session can't be renewed — callers decide what that means.
 */
export const refreshToken = async (): Promise<string> => {
  try {
    const response = await publicApiClient.post(REFRESH_URL, {
      refresh: localStorage.getItem(REFRESH_TOKEN_KEY) ?? "",
    });
    const token = response.data.access;
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    return token;
  } catch {
    return "";
  }
};

// Refresh the access token once on a 401 and replay the request. A session that
// can't be renewed is only worth acting on where a session was required.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const is401 = error?.response?.status === 401;
    const isRefresh = originalRequest?.url === REFRESH_URL;
    const isLoginPage = window.location.pathname === Routes.signin;

    if (is401 && !isRefresh && !isLoginPage && !isPublicPage() && !originalRequest._retry) {
      originalRequest._retry = true;
      const accessToken = await refreshToken();
      if (accessToken) {
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      }
      if (requiresSession()) {
        // Survive an expired session: the visitor is bounced to sign in and,
        // once they log back in, should still be offered the role they typed
        // on the landing page.
        preservePendingRole(() => localStorage.clear());
        window.location.assign(Routes.signin);
      }
    }
    return Promise.reject(error);
  }
);

interface ApiRequestProps {
  url: string;
  config?: AxiosRequestConfig;
  data?: unknown;
}

export async function getRequest(request: ApiRequestProps) {
  return await apiClient.get(request.url, request.config);
}

export async function postRequest(request: ApiRequestProps) {
  return await apiClient.post(request.url, request.data, request.config);
}

export async function putRequest(request: ApiRequestProps) {
  return await apiClient.put(request.url, request.data, request.config);
}

export async function patchRequest(request: ApiRequestProps) {
  return await apiClient.patch(request.url, request.data, request.config);
}

export async function deleteRequest(request: ApiRequestProps) {
  return await apiClient.delete(request.url, request.config);
}
